import { Link } from "react-router-dom";
import { ArrowRight, Headphones, Brain, Gamepad2, Heart, Sparkles } from "lucide-react";
import { trackClick, tutoringUrl } from "../lib/api";

export default function Landing() {
    return (
        <main className="container-page py-16 md:py-24 fade-up">
            <div className="grid md:grid-cols-12 gap-12 items-center">
                <div className="md:col-span-7">
                    <span
                        className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#E0EDE6] text-[#1F4237] text-xs font-semibold uppercase tracking-wider mb-5"
                        data-testid="landing-free-badge"
                    >
                        <Sparkles className="w-3.5 h-3.5" strokeWidth={2} /> Now free for everyone
                    </span>
                    <h1
                        className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.1] text-[#1C2321] mb-6"
                        style={{ fontFamily: "Outfit" }}
                        data-testid="landing-title"
                    >
                        Learn to read.<br />
                        On your own terms.<br />
                        <span className="text-[#2D5D4E]">At any age.</span>
                    </h1>
                    <p className="text-lg sm:text-xl text-[#515B57] leading-relaxed max-w-xl mb-10">
                        Lift Up Reading teaches the same proven Science of Reading taught to children &mdash;
                        but rebuilt for adults. No cartoons, no shame, and now <span className="font-medium text-[#1C2321]">no cost</span>.
                        Just the five steps that actually work, with practice games to make them stick.
                    </p>
                    <div className="flex flex-wrap gap-4">
                        <Link
                            to="/register"
                            data-testid="landing-cta-start"
                            onClick={() => trackClick("landing", "internal:register")}
                            className="inline-flex items-center gap-2 px-8 py-4 rounded-lg bg-[#2D5D4E] text-[#FAF9F6] text-lg font-medium hover:bg-[#1F4237] transition-all hover:-translate-y-0.5"
                        >
                            Start reading — it's free <ArrowRight className="w-5 h-5" strokeWidth={1.8} />
                        </Link>
                        <Link
                            to="/login"
                            data-testid="landing-cta-signin"
                            className="inline-flex items-center gap-2 px-8 py-4 rounded-lg border border-[#E5E7E2] bg-white text-[#1C2321] text-lg font-medium hover:bg-[#E8E2D2] transition-all"
                        >
                            I already have an account
                        </Link>
                    </div>
                </div>

                <div className="md:col-span-5 relative">
                    <img
                        src="https://images.unsplash.com/photo-1544716278-ca5e3f4abd8c?auto=format&fit=crop&w=1200&q=80"
                        alt="Adult reading a book in a quiet library"
                        className="rounded-2xl w-full h-[460px] object-cover"
                        style={{ filter: "saturate(0.85)" }}
                        data-testid="landing-hero-image"
                    />
                    <div className="absolute -bottom-6 -left-6 bg-white border border-[#E5E7E2] rounded-xl p-5 max-w-xs shadow-sm hidden md:block">
                        <p className="label-eyebrow mb-2">From a learner</p>
                        <p className="text-[#1C2321] text-base leading-relaxed italic">
                            "I'm forty-six. I finished my first novel last month. I cried."
                        </p>
                    </div>
                </div>
            </div>

            <section className="mt-24">
                <p className="label-eyebrow mb-3">How it works</p>
                <h2 className="text-3xl sm:text-4xl font-medium text-[#1C2321] mb-12 max-w-2xl" style={{ fontFamily: "Outfit" }}>
                    Five steps, the way reading actually gets built in the brain.
                </h2>
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 stagger">
                    {[
                        { icon: Brain, title: "Phonemic Awareness", body: "Train your ear to hear the small sounds inside spoken words." },
                        { icon: Headphones, title: "Phonics", body: "Match those sounds to letters so you can decode anything." },
                        { icon: Gamepad2, title: "Games", body: "Six tactile games turn every lesson into something that sticks." },
                        { icon: Heart, title: "A patient tutor", body: "Ask any question, any time. No judgment. Real answers." },
                    ].map((f) => (
                        <div key={f.title} className="card-rw p-7" data-testid={`landing-feature-${f.title.toLowerCase().replace(/ /g, "-")}`}>
                            <f.icon className="w-7 h-7 text-[#2D5D4E] mb-5" strokeWidth={1.5} />
                            <h3 className="text-xl font-medium mb-2" style={{ fontFamily: "Outfit" }}>{f.title}</h3>
                            <p className="text-[#515B57] leading-relaxed">{f.body}</p>
                        </div>
                    ))}
                </div>
            </section>

            <section className="mt-24 bg-white border border-[#E5E7E2] rounded-2xl p-10 md:p-14">
                <div className="grid md:grid-cols-2 gap-10 items-center">
                    <div>
                        <p className="label-eyebrow mb-3">Built for adults</p>
                        <h2 className="text-3xl font-medium text-[#1C2321] mb-5" style={{ fontFamily: "Outfit" }}>
                            No baby pictures. No talking animals. No quizzes that feel like a test.
                        </h2>
                        <p className="text-lg text-[#515B57] leading-relaxed">
                            One in five adults reads below a sixth-grade level. Most learning tools are made
                            for children, and using them in public is its own kind of pain. We rebuilt the whole
                            experience to feel like the kind of app you'd be proud to open on the bus.
                        </p>
                    </div>
                    <img
                        src="https://images.unsplash.com/photo-1506880018603-83d5b814b5a6?auto=format&fit=crop&w=1200&q=80"
                        alt="An adult reading a book"
                        className="rounded-xl w-full h-80 object-cover"
                        style={{ filter: "saturate(0.85)" }}
                        data-testid="landing-adults-image"
                    />
                </div>
            </section>

            <section className="mt-24 bg-white border border-[#E5E7E2] rounded-2xl p-10 md:p-14">
                <div className="grid md:grid-cols-2 gap-10 items-center">
                    <div>
                        <p className="label-eyebrow mb-3" style={{ color: "#D47A55" }}>Prefer a real teacher?</p>
                        <h2 className="text-3xl font-medium text-[#1C2321] mb-5" style={{ fontFamily: "Outfit" }}>
                            One-on-one virtual tutoring, from our sister company.
                        </h2>
                        <p className="text-lg text-[#515B57] leading-relaxed mb-6">
                            Some learners thrive with the app alone. Others do best with a live coach on their shoulder.
                            <strong className="text-[#1C2321]"> Lift Up Literacy LLC</strong> offers weekly virtual tutoring for students and adults
                            &mdash; certified reading teachers, structured lessons, real accountability.
                        </p>
                        <a
                            href={tutoringUrl("landing")}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => trackClick("landing", "liftupliteracy.com")}
                            data-testid="landing-virtual-tutoring-cta"
                            className="inline-flex items-center gap-2 px-7 py-3.5 rounded-lg bg-[#D47A55] text-white text-lg font-medium hover:bg-[#B86644] transition-all hover:-translate-y-0.5"
                        >
                            Explore virtual tutoring
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M7 17l9.2-9.2M17 17V7H7"/></svg>
                        </a>
                        <Link
                            to="/bundle"
                            onClick={() => trackClick("landing", "internal:bundle")}
                            data-testid="landing-bundle-link"
                            className="ml-4 inline-flex items-center gap-1 text-[#515B57] hover:text-[#2D5D4E] text-base font-medium underline underline-offset-4 decoration-[#E5E7E2]"
                        >
                            See the full bundle
                        </Link>
                    </div>
                    <img
                        src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?auto=format&fit=crop&w=1200&q=80"
                        alt="A tutor and learner working together"
                        className="rounded-xl w-full h-80 object-cover"
                        style={{ filter: "saturate(0.85)" }}
                    />
                </div>
            </section>
        </main>
    );
}
